/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

/**
 * @module carousel-add-slide/bulbimagecarouselremoveslideui
 */

import { Plugin, ButtonView, type Locale } from 'ckeditor5';
import bulbRemoveSlideIcon from '../../../assets/plugin_icons/layer-minus-regular.svg';
import { windowsEventListenerChecklist } from '../../utils';

/**
 * The image carousel remove slide plugin class.
 *
 * @extends Plugin
 */
export default class BulbImageCarouselRemoveSlideUI extends Plugin {

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbImageCarouselRemoveSlideUI' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    const { t } = editor;
    let activeImageCarouselSlideIndex: number;

    editor.ui.componentFactory.add('bulbRemoveSlide', (locale: Locale): ButtonView => {
      // The button will be an instance of ButtonView.
      const buttonView = new ButtonView(locale);
      if (!windowsEventListenerChecklist.removeCarouselImageAdded) {
        windowsEventListenerChecklist.removeCarouselImageAdded = true;
        window.addEventListener('handleCarouselImageDeletion', this.handleCarouselImageDeletion);
      }
      buttonView.set({
        label: t('editor_image_carousel_bar_remove_slide'),
        icon: bulbRemoveSlideIcon,
        tooltip: true,
      });

      // Callback executed once the button is clicked:
      buttonView.on('execute', () => {
        const modelElement = editor.model.document.selection.getSelectedElement();
        const contentBlock = CKEBulbData.page.unit.contentBlocks[modelElement.getAttribute('bulb-block-id') as string];
        const blockId = modelElement.getAttribute('bulb-block-id');
        const currentIndexEvent = new CustomEvent('currentSlideIndex', {
          detail: {
            blockId,
          },
        });
        window.dispatchEvent(currentIndexEvent);

        if (contentBlock.carouselImages.length <= 1) {
          return window.dispatchEvent(new CustomEvent('bulbShowBlockDeleteConfirmation', {
            detail: {
              editor,
            },
          }));
        }

        // Dispatch event for opening delete modal.
        const showDeleteModalForCarouselImageSlideEvent = new CustomEvent(
          'bulbShowBlockDeleteConfirmation',
          {
            detail: {
              editor,
              imageCarouselSlide: { blockId, activeImageCarouselSlideIndex },
            },
          },
        );
        window.dispatchEvent(showDeleteModalForCarouselImageSlideEvent);
      });

      // Listen for the event that is going to be fired from our client for the current image index.
      window.addEventListener('dispatchSlideIndex', (event: any) => {
        activeImageCarouselSlideIndex = event.detail.activeIndex;
        if (event.detail.activeIndex === -1) {
          activeImageCarouselSlideIndex = 0;
        }
      });

      return buttonView;
    });
  }

  /**
   * Handle the image deletion from a carousel.
   * @param event Event which will contains details of an image slide.
   */
  private handleCarouselImageDeletion(event: CustomEvent): void {
    const { imageCarouselSlide } = event.detail;
    if (imageCarouselSlide) {
      const handleCarouselImageDeletionEvent = new CustomEvent('removeImageFromCarousel', {
        detail: {
          eventFiredFromEditor: true,
          blockId: imageCarouselSlide.blockId,
          imageIndex: imageCarouselSlide.activeImageCarouselSlideIndex,
        },
      });
      window.dispatchEvent(handleCarouselImageDeletionEvent);
    }
  }

  /**
   * Remove event on plugin destroy.
   */
  public override destroy(): void {
    window.removeEventListener('handleCarouselImageDeletion', this.handleCarouselImageDeletion);
    windowsEventListenerChecklist.removeCarouselImageAdded = false;
  }
}
